import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CapacitorService } from './capacitor.service';

/**
 * Network State Service
 * Provides real-time network connectivity status and events
 * Used by offline-first services to determine when to sync
 */
export interface NetworkStatus {
  isOnline: boolean;
  type?: string; // 'wifi', '4g', '3g', etc.
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class NetworkStateService {
  private networkStatus$ = new BehaviorSubject<NetworkStatus>({
    isOnline: this.isOnline(),
    timestamp: Date.now()
  });

  constructor(private capacitor: CapacitorService) {
    this.initializeNetworkMonitoring();
    this.refreshCurrentStatus();
  }

  private initializeNetworkMonitoring(): void {
    // Subscribe to Capacitor network status changes
    this.capacitor.getNetworkStatusObservable().subscribe((status) => {
      this.networkStatus$.next({
        isOnline: status.isOnline,
        timestamp: Date.now()
      });
    });
  }

  private refreshCurrentStatus(): void {
    this.capacitor.checkNetworkStatus().then((status) => {
      this.networkStatus$.next({
        isOnline: status.isOnline,
        type: status.type,
        timestamp: Date.now()
      });
    }).catch(() => {
      this.networkStatus$.next({
        isOnline: this.isOnline(),
        timestamp: Date.now()
      });
    });
  }

  /**
   * Public refresh to re-evaluate the current network status on demand.
   */
  refreshNow(): void {
    this.refreshCurrentStatus();
  }

  /**
   * Get current network status
   */
  getNetworkStatus(): NetworkStatus {
    return this.networkStatus$.getValue();
  }

  /**
   * Observable of network status changes
   */
  getNetworkStatusObservable(): Observable<NetworkStatus> {
    return this.networkStatus$.asObservable();
  }

  /**
   * Check if currently online
   */
  isOnline(): boolean {
    if (typeof navigator === 'undefined') {
      return true;
    }
    return navigator.onLine;
  }

  /**
   * Check if currently offline
   */
  isOffline(): boolean {
    return !this.isOnline();
  }

  /**
   * Wait until online before executing callback
   */
  waitUntilOnline(): Promise<void> {
    return new Promise((resolve) => {
      if (this.isOnline()) {
        resolve();
        return;
      }

      const subscription = this.networkStatus$.subscribe((status) => {
        if (status.isOnline) {
          subscription.unsubscribe();
          resolve();
        }
      });
    });
  }

  /**
   * Execute callback when connection is restored
   */
  onConnectionRestored(callback: () => void): () => void {
    let wasOnline = this.isOnline();

    const subscription = this.networkStatus$.subscribe((status) => {
      if (!wasOnline && status.isOnline) {
        callback();
      }
      wasOnline = status.isOnline;
    });

    // Return unsubscribe function
    return () => subscription.unsubscribe();
  }

  /**
   * Execute callback when connection is lost
   */
  onConnectionLost(callback: () => void): () => void {
    let wasOnline = this.isOnline();

    const subscription = this.networkStatus$.subscribe((status) => {
      if (wasOnline && !status.isOnline) {
        callback();
      }
      wasOnline = status.isOnline;
    });

    // Return unsubscribe function
    return () => subscription.unsubscribe();
  }

  /**
   * Get time since last online (in milliseconds)
   */
  getTimeSinceLastOnline(): number {
    const status = this.networkStatus$.getValue();
    if (status.isOnline) {
      return 0;
    }
    return Date.now() - status.timestamp;
  }
}
